import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t.nav.howItWorks, href: '#how-it-works' },
    { label: t.nav.whyWatrly, href: '#why-watrly' },
    { label: t.nav.platform, href: '#platform' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-watrly-cream/95 backdrop-blur-xl shadow-lg shadow-watrly-desert/10 border-b border-watrly-sand-warm/50' 
          : 'bg-transparent'
      }`}
    >
      <div className="container-watrly">
        <nav className="flex items-center justify-between h-20 lg:h-24">
          <a href="/" className="relative z-10 flex items-center gap-3">
            <img 
              src="/assets/images/watrly-logo.png" 
              alt="Watrly" 
              className="h-10 lg:h-12 w-auto"
            />
            <span className="text-xl lg:text-2xl font-bold text-watrly-slate">
              Watrly
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-12">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-sm font-medium text-watrly-slate/70 hover:text-watrly-turquoise transition-colors duration-300"
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              className="px-4 py-2 text-sm font-semibold text-watrly-turquoise border-2 border-watrly-turquoise/20 rounded-full hover:bg-watrly-turquoise/5 transition-all duration-300"
            >
              {language === 'ar' ? 'EN' : 'عربي'}
            </button>
            <a href="/login"
              className="px-5 py-2.5 text-sm font-semibold text-watrly-slate border-2 border-watrly-slate/20 rounded-full hover:bg-watrly-slate/5 transition-all duration-300">
              {language === 'ar' ? 'تسجيل الدخول' : 'Login'}
            </a>
            <a href="/register" className="btn-primary">
              {language === 'ar' ? 'إنشاء حساب' : 'Sign Up'}
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative z-10 p-2"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <motion.span
                animate={isMobileMenuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                className="w-full h-0.5 bg-watrly-slate origin-left transition-colors"
              />
              <motion.span
                animate={isMobileMenuOpen ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
                className="w-full h-0.5 bg-watrly-slate"
              />
              <motion.span
                animate={isMobileMenuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                className="w-full h-0.5 bg-watrly-slate origin-left transition-colors"
              />
            </div>
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-watrly-slate/10"
          >
            <div className="container-watrly py-6 flex flex-col gap-4">
              {navItems.map((item, index) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-lg font-medium text-watrly-slate/70 hover:text-watrly-turquoise py-2"
                >
                  {item.label}
                </motion.a>
              ))}
              <motion.button
                onClick={toggleLanguage}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="px-4 py-3 text-sm font-semibold text-watrly-turquoise border-2 border-watrly-turquoise/20 rounded-full hover:bg-watrly-turquoise/5 transition-all duration-300"
              >
                {language === 'ar' ? 'English' : 'عربي'}
              </motion.button>
              <motion.a
                href="/login"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full text-center px-4 py-3 text-sm font-semibold text-watrly-slate border-2 border-watrly-slate/20 rounded-full hover:bg-watrly-slate/5 transition-all"
              >
                {language === 'ar' ? 'تسجيل الدخول' : 'Login'}
              </motion.a>
              <motion.a
                href="/register"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                onClick={() => setIsMobileMenuOpen(false)}
                className="btn-primary mt-1 text-center"
              >
                {language === 'ar' ? 'إنشاء حساب' : 'Sign Up'}
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
